import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, productImage, TYPE_LABELS, CATEGORIES, categoryOf, workflowBadge, scoreColor, MODE_LABELS, modeBadge, channelStatusBadge, shopifyStatusLabel } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Loader2, RefreshCw, Copy, Check, Send, FileText, Tag as TagIcon,
  Sliders, DollarSign, Trash2, ImageIcon, Share2, ExternalLink, Factory, Rocket,
  AlertTriangle, CheckCircle2, ChevronDown, RotateCcw, X, Wrench, AlignCenterVertical, Truck, Shapes,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScoreBreakdown } from "@/components/ScoreBreakdown";
import { ChannelPublish } from "@/components/ChannelPublish";

const WF = { review: "Needs review", draft: "Draft", published: "Published" };
const AUTOMATION = [
  { key: "manual", label: "Manual review" },
  { key: "draft", label: "Draft only" },
  { key: "auto", label: "Auto-publish" },
];

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={`copy-${id}`}
      onClick={() => { navigator.clipboard.writeText(text || ""); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1200); }}
      className="text-zinc-400 hover:text-blue-600 transition-colors"
    >
      {done ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState(false);
  const [filters, setFilters] = useState({ workflow_state: "", product_type: "", mode: "", source_event: "" });
  const [events, setEvents] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [audit, setAudit] = useState({ loading: false, active: false, map: {}, count: 0, savings: 0 });
  const [providerInfo, setProviderInfo] = useState(null);
  const [altModal, setAltModal] = useState({ open: false, sourceIds: [], types: new Set() });
  const [altJob, setAltJob] = useState(null);
  const altPollRef = useRef(null);
  const gridRef = useRef(null);
  const didMountFilter = useRef(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [printifyConfigured, setPrintifyConfigured] = useState(false);
  const [batchJob, setBatchJob] = useState(null);
  const [batchN, setBatchN] = useState(10);
  const [batchMode, setBatchMode] = useState("ready_made");
  const [batchTypes, setBatchTypes] = useState([]);
  const [batchCats, setBatchCats] = useState([]);
  const [batchThemes, setBatchThemes] = useState([]);
  const [pipelineN, setPipelineN] = useState(10);
  const [pipelineFresh, setPipelineFresh] = useState(true);
  const [pipelineTypes, setPipelineTypes] = useState([]);
  const [pipelineThemes, setPipelineThemes] = useState([]);
  const [pipelineAvail, setPipelineAvail] = useState(null);
  const [themes, setThemes] = useState([]);
  const [resetBusy, setResetBusy] = useState(false);
  const [recoverJob, setRecoverJob] = useState(null);
  const [recenterJob, setRecenterJob] = useState(null);
  const recoverRef = useRef(null);
  const [dups, setDups] = useState([]);
  const [keepSel, setKeepSel] = useState({});
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [pfStatus, setPfStatus] = useState(null);
  const [pfBusy, setPfBusy] = useState(false);

  const loadPfStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/products/printify-status");
      setPfStatus(data);
    } catch { setPfStatus(null); }
  }, []);
  useEffect(() => { loadPfStatus(); }, [loadPfStatus]);
  const pollRef = useRef(null);
  const recenterPollRef = useRef(null);

  const loadReadiness = useCallback(async () => {
    try {
      const { data } = await api.get("/products/readiness?scope=unpublished");
      setReadiness(data);
    } catch { setReadiness(null); }
  }, []);

  useEffect(() => { loadReadiness(); }, [loadReadiness]);

  const loadDups = useCallback(async () => {
    try {
      const { data } = await api.get("/products/duplicates");
      setDups(data.groups || []);
    } catch { setDups([]); }
  }, []);

  useEffect(() => { loadDups(); }, [loadDups]);

  const runDedupe = async (group) => {
    const gkey = group.members[0].id;
    const keep = keepSel[gkey] || group.keep_id;
    const remove_ids = group.members.filter((m) => m.id !== keep).map((m) => m.id);
    if (!remove_ids.length) return;
    if (!window.confirm(`Remove ${remove_ids.length} duplicate listing(s)? This also deletes them from Printify → Shopify and cannot be undone.`)) return;
    setDedupeBusy(true);
    try {
      const { data } = await api.post("/products/dedupe", { remove_ids });
      toast.success(`Removed ${data.removed} duplicate${data.removed > 1 ? "s" : ""}${data.printify_deleted ? ` · ${data.printify_deleted} delisted from Shopify` : ""}`);
      if (data.errors?.length) toast.error(data.errors[0]);
      await loadDups();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally { setDedupeBusy(false); }
  };

  useEffect(() => {
    api.get("/channels").then(({ data }) => setPrintifyConfigured(!!data.printify_configured)).catch(() => {});
    api.get("/themes").then(({ data }) => setThemes(data)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams({ mode: "ready_made" });
    if (pipelineTypes.length) qs.set("types", pipelineTypes.join(","));
    if (pipelineThemes.length) qs.set("themes", pipelineThemes.join(","));
    api.get(`/ideas/availability?${qs.toString()}`)
      .then(({ data }) => setPipelineAvail(data.count))
      .catch(() => setPipelineAvail(null));
  }, [pipelineTypes, pipelineThemes, batchJob?.status]);

  const load = useCallback(async (selId) => {
    const qs = new URLSearchParams();
    if (filters.workflow_state) qs.set("workflow_state", filters.workflow_state);
    if (filters.product_type) qs.set("product_type", filters.product_type);
    if (filters.mode) qs.set("mode", filters.mode);
    if (filters.source_event) qs.set("source_event", filters.source_event);
    const { data } = await api.get(`/products?${qs.toString()}`);
    setProducts(data);
    setLoading(false);
    const wantId = selId || params.get("id");
    if (wantId) {
      const found = data.find((p) => p.id === wantId);
      if (found) setSelected(found);
    } else if (!selected && data.length) {
      setSelected(data[0]);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // When the user changes a filter, bring the results into view (they sit below several panels,
  // so an off-screen update used to feel like nothing happened).
  useEffect(() => {
    if (!didMountFilter.current) { didMountFilter.current = true; return; }
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [filters]);

  useEffect(() => {
    api.get("/products/events").then(({ data }) => setEvents(data || [])).catch(() => setEvents([]));
  }, [batchJob?.status]);

  useEffect(() => {
    if (!selected?.id) { setProviderInfo(null); return; }
    let cancelled = false;
    setProviderInfo(null);
    api.get(`/products/${selected.id}/provider-info`)
      .then(({ data }) => { if (!cancelled) setProviderInfo(data); })
      .catch(() => { if (!cancelled) setProviderInfo(null); });
    return () => { cancelled = true; };
  }, [selected?.id]);

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(products.map((p) => p.id)));

  const runShippingFinder = async () => {
    setAudit((a) => ({ ...a, loading: true }));
    try {
      const { data } = await api.get("/products/shipping-audit?threshold=10");
      if (!data.count) {
        toast.success("No products on high-shipping providers — you're all set.");
        setAudit({ loading: false, active: false, map: {}, count: 0, savings: 0 });
        return;
      }
      const map = {};
      let savings = 0;
      data.products.forEach((p) => { map[p.id] = p; savings += p.savings || 0; });
      setAudit({ loading: false, active: true, map, count: data.count, savings: Math.round(savings * 100) / 100 });
      // auto-select the flagged products that are currently loaded
      setSelectedIds(new Set(products.filter((p) => map[p.id]).map((p) => p.id)));
      toast.success(`${data.count} product(s) on pricey providers found — selected and ready to Fix & republish.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the shipping finder");
      setAudit((a) => ({ ...a, loading: false }));
    }
  };

  const clearFinder = () => { setAudit({ loading: false, active: false, map: {}, count: 0, savings: 0 }); clearSelection(); };

  const checkMarginFloor = async (ids) => {
    try {
      const { data } = await api.post("/products/margin-floor-check", { product_ids: ids });
      if (!data.enabled || !data.blocked_count) return { proceed: true, override: false };
      const names = data.blocked.slice(0, 8).map((b) => `• ${(b.title || "").slice(0, 40)} — ${b.no_data ? (b.reason === "no_cost" ? "no cost loaded" : "no price set") : b.margin + "% margin"}`).join("\n");
      const more = data.blocked_count > 8 ? `\n…and ${data.blocked_count - 8} more` : "";
      const ok = window.confirm(`${data.blocked_count} of ${data.total} selected product(s) are below your ${data.floor_pct}% retail-margin publish floor (or have no cost loaded):\n\n${names}${more}\n\nOK = publish them ANYWAY (override the floor this time)\nCancel = stop — fix prices or deselect them first`);
      return { proceed: ok, override: ok };
    } catch {
      return { proceed: true, override: false }; // never block a publish just because the check failed
    }
  };

  const publishSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Send ${ids.length} selected product(s) to Printify now? Each is created in Printify and auto-synced to Shopify (paced to avoid errors).`)) return;
    const gate = await checkMarginFloor(ids);
    if (!gate.proceed) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/batch-publish-printify", { product_ids: ids, public_base: window.location.origin, override_margin: gate.override });
      toast.success(`Publishing ${data.count} product(s)… watch the publish panel for progress.`);
      clearSelection();
      pollJob(data.job_id, "selected product(s) now live on your Shopify store");
      setBatchJob({ id: data.job_id, status: "running", completed: 0, total: data.count });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish selected products");
    } finally { setBulkBusy(false); }
  };

  const fixRepublishSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Fix & republish ${ids.length} product(s)? This recenters each design, DELETES the old Printify/Shopify listing, and republishes a fresh, correctly-sized one. Old product links are replaced with new ones.`)) return;
    const gate = await checkMarginFloor(ids);
    if (!gate.proceed) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/fix-republish", { product_ids: ids, public_base: window.location.origin, override_margin: gate.override });
      toast.success(`Fixing & republishing ${data.count} product(s)… this runs in the background.`);
      clearSelection();
      pollJob(data.job_id, "product(s) fixed and republished to Shopify");
      setBatchJob({ id: data.job_id, status: "running", completed: 0, total: data.count });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start fix & republish");
    } finally { setBulkBusy(false); }
  };


  const setBulkAutomation = async (mode) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      await api.post("/products/bulk-automation", { ids, mode });
      const label = mode === "auto" ? "Auto-publish" : mode === "draft" ? "Draft only" : "Manual review";
      toast.success(`Set ${ids.length} product(s) to ${label}.`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update automation mode");
    } finally { setBulkBusy(false); }
  };

  const updateProduct = async (patch) => {
    const { data } = await api.patch(`/products/${selected.id}`, patch);
    setSelected(data);
    setProducts((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    return data;
  };

  const setAutomation = async (mode) => {
    const data = await updateProduct({ automation_mode: mode });
    if (mode === "auto" && data?.auto_publish_started) {
      toast.success("Auto-publish on — pushing this product live to Printify → Shopify now…");
      setTimeout(load, 6000);
    } else if (mode === "auto") {
      toast.info("Auto-publish set. It will publish once Printify is connected and a design/mockup exists (or if not already live).");
    } else {
      toast.success(`Automation set to ${mode === "draft" ? "Draft only" : "Manual review"}`);
    }
  };

  const eligibleCount = products.filter(
    (p) => (p.design_path || p.mockup_path) && p.channels?.printify?.status !== "published"
      && (batchMode === "all" || p.mode === batchMode)
      && (batchTypes.length === 0 || batchTypes.includes(p.product_type))
      && (batchCats.length === 0 || batchCats.includes(categoryOf(p)))
      && (batchThemes.length === 0 || batchThemes.includes(p.theme_key))
  ).length;

  const pollJob = (jobId, doneMsg) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        setBatchJob(j);
        if (["done", "error", "cancelled"].includes(j.status)) {
          clearInterval(pollRef.current);
          if (j.status === "done") toast.success(`${j.result?.published || 0} ${doneMsg}`);
          else if (j.status === "cancelled") toast.info("Job cancelled");
          else toast.error("Job hit an error — check the log");
          load();
          loadDups();
          loadReadiness();
          loadPfStatus();
        }
      } catch { clearInterval(pollRef.current); }
    }, 3000);
  };

  const openAlternates = (sourceIds) => {
    if (!sourceIds.length) return;
    setAltModal({ open: true, sourceIds, types: new Set() });
  };

  const toggleAltType = (t) => setAltModal((m) => {
    const types = new Set(m.types);
    types.has(t) ? types.delete(t) : types.add(t);
    return { ...m, types };
  });

  const submitAlternates = async () => {
    const target_types = [...altModal.types];
    if (!target_types.length) { toast.error("Pick at least one product type"); return; }
    try {
      const { data } = await api.post("/products/alternates", { source_ids: altModal.sourceIds, target_types });
      toast.success(`Creating ${data.count} alternate product(s) in the background — they'll appear here as "Needs review".`);
      setAltModal({ open: false, sourceIds: [], types: new Set() });
      clearInterval(altPollRef.current);
      altPollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setAltJob(j);
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(altPollRef.current);
            if (j.status === "done") toast.success(`Done — ${j.result?.created || 0} alternate(s) created under "Needs review"${j.result?.skipped ? `, ${j.result.skipped} skipped` : ""}. Filter Status → Needs review to see them.`);
            else if (j.status === "error") toast.error("Alternates job hit an error — check the log");
            setTimeout(() => setAltJob(null), 4000);
            load();
          }
        } catch { clearInterval(altPollRef.current); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create alternates");
    }
  };

  const pollRecenter = (jobId, expected) => {
    clearInterval(recenterPollRef.current);    recenterPollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/jobs/${jobId}`);
        setRecenterJob(j);
        if (["done", "error", "cancelled"].includes(j.status)) {
          clearInterval(recenterPollRef.current);
          if (j.status === "done") toast.success(`Recentered ${j.result?.recentered || 0} design(s).`);
          else if (j.status === "error") toast.error("Recentering was interrupted — click Resume to continue.");
          load();
        }
      } catch { clearInterval(recenterPollRef.current); }
    }, 3000);
  };

  const recenterDesigns = async () => {
    if (!window.confirm("Re-center the artwork on your existing designs? This repositions the art you already have and re-renders the previews — no new AI art and no credits used. It safely resumes if it was interrupted before.")) return;
    try {
      const { data } = await api.post("/products/recenter-designs");
      setRecenterJob({ id: data.job_id, status: "running", completed: 0, total: data.count });
      toast.info(`Recentering ${data.count} design(s)…`);
      pollRecenter(data.job_id, data.count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start recentering");
    }
  };

  const clearRecenter = async () => {
    if (recenterJob?.id) {
      try { await api.post(`/jobs/${recenterJob.id}/cancel`); } catch { /* ignore */ }
    }
    clearInterval(recenterPollRef.current);
    setRecenterJob(null);
    toast.success("Cleared. You can start recentering again.");
  };


  const cancelBatchJob = async () => {
    if (!batchJob?.id) return;
    try {
      await api.post(`/jobs/${batchJob.id}/cancel`);
      clearInterval(pollRef.current);
      setBatchJob((b) => (b ? { ...b, status: "cancelled" } : b));
      toast.success("Job cancelled — the pipeline stops at the next product");
      load(); loadPfStatus(); loadReadiness();
    } catch {
      toast.error("Could not cancel — it may have already finished");
    }
  };

  // Surface a bulk build/publish job that's still 'running' (e.g. left over after a redeploy) so it
  // can be cancelled — otherwise the controls stay disabled with no way out.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/jobs");
        const running = (data || []).find(
          (j) => j.status === "running" && ["batch_printify", "readymade_pipeline"].includes(j.kind)
        );
        if (running) { setBatchJob(running); pollJob(running.id, "product(s) now live on your Shopify store"); }
        const rc = (data || []).find((j) => j.status === "running" && j.kind === "recenter_designs");
        if (rc) { setRecenterJob(rc); pollRecenter(rc.id, rc.total); }
      } catch { /* ignore */ }
    })();
    return () => { clearInterval(pollRef.current); clearInterval(recoverRef.current); clearInterval(recenterPollRef.current); clearInterval(altPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshPfStatus = async () => {
    if (pfBusy) return;
    setPfBusy(true);
    try {
      const { data } = await api.post("/products/printify-verify", {});
      if (!data.job_id) { toast.info("Nothing to reconcile — all publishes are resolved."); await loadPfStatus(); return; }
      toast.info(`Checking ${data.count} product(s) against Printify…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          if (["done", "error"].includes(j.status)) {
            clearInterval(poll);
            if (j.status === "done") toast.success(`Reconciled — ${j.result?.now_published || 0} now live, ${j.result?.still_failed || 0} still failing`);
            await loadPfStatus(); await load();
          }
        } catch { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not refresh status");
    } finally { setPfBusy(false); }
  };

  const retryFailed = async (ids) => {
    if (pfBusy) return;
    setPfBusy(true);
    try {
      const { data } = await api.post("/products/retry-printify", ids ? { product_ids: ids } : {});
      toast.info(`Retrying ${data.count} publish(es)…`);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          if (["done", "error"].includes(j.status)) {
            clearInterval(poll);
            if (j.status === "done") toast.success(`Retry done — ${j.result?.republished || 0} published, ${j.result?.still_failed || 0} still failing`);
            else toast.error("Retry hit an error");
            await loadPfStatus(); await load();
          }
        } catch { clearInterval(poll); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed to start");
    } finally { setPfBusy(false); }
  };

  const batchPublish = async () => {
    if (batchJob?.status === "running") return;
    try {
      const { data } = await api.post("/products/batch-publish-printify", {
        count: batchN, mode: batchMode, product_types: batchTypes, categories: batchCats, themes: batchThemes, public_base: window.location.origin,
      });
      toast.success(`Publishing ${data.count} product${data.count > 1 ? "s" : ""} to Printify → Shopify…`);
      setBatchJob({ id: data.job_id, status: "running", completed: 0, total: data.count, log: [] });
      pollJob(data.job_id, "product(s) now live on your Shopify store");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch publish failed");
    }
  };

  const runReadymadePipeline = async () => {
    if (batchJob?.status === "running") return;
    try {
      const { data } = await api.post("/products/build-publish-readymade", {
        count: pipelineN, generate_fresh: pipelineFresh, product_types: pipelineTypes, themes: pipelineThemes, public_base: window.location.origin,
      });
      toast.success(`Building + publishing up to ${data.count} ready-made products…`);
      setBatchJob({ id: data.job_id, status: "running", completed: 0, total: data.count, log: [] });
      pollJob(data.job_id, "ready-made product(s) now live on your Shopify store");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pipeline failed to start");
    }
  };

  const regenerateMockup = async () => {
    setPreviewBusy(true);
    const has = !!selected.preview_path;
    toast.info(has ? "Regenerating print design & preview…" : "Generating print design & preview…");
    try {
      const { data } = await api.post(`/products/${selected.id}/preview-print${has ? "?regenerate=true" : ""}`);
      const updated = { ...selected, preview_path: data.preview_path, design_path: data.design_path };
      setSelected(updated);
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      toast.success("Print design updated — this is exactly what prints");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setPreviewBusy(false); }
  };

  const resetStorefront = async () => {
    if (!window.confirm("Start over? This deletes ALL products from Printify and removes any leftover Honorifica listings from your Shopify store (including orphans). Products stay in Heartline so you can re-publish them with the corrected scale. This is irreversible.")) return;
    setResetBusy(true);
    toast.info("Wiping published products from Printify → Shopify…");
    try {
      const { data } = await api.post("/products/reset-storefront");
      const total = (data.printify_deleted || 0) + (data.shopify_deleted || 0);
      toast.success(`Wiped ${total} listing${total === 1 ? "" : "s"} · Printify ${data.printify_deleted}, Shopify ${data.shopify_deleted}`);
      if (data.errors?.length) toast.error(`${data.errors.length} item(s) couldn't be removed — see console`, { description: data.errors[0] });
      load();
      loadDups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetBusy(false); }
  };

  const republishStuck = async () => {
    if (!window.confirm("Re-publish any products stuck in Printify's 'publishing error' state? This reads each listing's true state and only re-sends the stranded ones to Shopify (variants only — your gallery images are untouched). Safe to run anytime.")) return;
    clearInterval(recoverRef.current);
    try {
      const { data } = await api.post("/products/republish-stuck");
      if (!data.job_id) { toast.info("No Printify listings found to check."); return; }
      toast.info(`Checking ${data.count} listing(s) and re-publishing any stuck…`);
      setRecoverJob({ status: "running", completed: 0, total: data.count });
      recoverRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setRecoverJob(j);
          if (["done", "error", "cancelled"].includes(j.status)) {
            clearInterval(recoverRef.current);
            if (j.status === "done") {
              const r = j.result || {};
              toast.success(`Recovery complete — ${r.fixed || 0} re-published, ${r.skipped || 0} already ok, ${r.still_stuck || 0} still failing`);
            } else toast.error("Recovery hit an error — check the log");
            load(); loadPfStatus();
          }
        } catch { clearInterval(recoverRef.current); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start recovery");
    }
  };

  const remove = async () => {
    await api.delete(`/products/${selected.id}`);
    toast.success("Product deleted");
    setSelected(null);
    load();
    loadDups();
  };

  const copyAll = () => {
    if (!selected) return;
    const txt = `TITLE:\n${selected.listing_title}\n\nDESCRIPTION:\n${selected.description}\n\nTAGS:\n${(selected.tags || []).join(", ")}`;
    navigator.clipboard.writeText(txt);
    toast.success("Full listing copied to clipboard");
  };

  const genSocial = async () => {
    setSocialBusy(true);
    toast.info("Writing social posts for all channels…");
    try {
      const { data } = await api.post("/social/generate", { product_id: selected.id, occasion: selected.source_event || "" });
      toast.success(`Generated ${data.length} social posts — see Social tab`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Social generation failed");
    } finally { setSocialBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="products-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Product Workspace</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Products &amp; listings</h1>
        </div>
        <div className="flex gap-2">
          <select value={filters.workflow_state} onChange={(e) => setFilters((f) => ({ ...f, workflow_state: e.target.value }))} data-testid="filter-state" className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All states</option>
            <option value="review">Needs review</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <select value={filters.product_type} onChange={(e) => setFilters((f) => ({ ...f, product_type: e.target.value }))} data-testid="filter-type" className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select value={filters.mode} onChange={(e) => setFilters((f) => ({ ...f, mode: e.target.value }))} data-testid="filter-mode" className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">All products</option>
            <option value="personalized">Personalized</option>
            <option value="ready_made">Ready-made</option>
          </select>
          {events.length > 0 && (
            <select value={filters.source_event} onChange={(e) => { setFilters((f) => ({ ...f, source_event: e.target.value })); clearSelection(); }} data-testid="filter-event" className="bg-white border border-violet-300 text-violet-800 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none">
              <option value="">All events</option>
              {events.map((ev) => (
                <option key={ev.event} value={ev.event}>{ev.event} ({ev.unpublished} unpublished / {ev.count})</option>
              ))}
            </select>
          )}
          <button onClick={audit.active ? clearFinder : runShippingFinder} disabled={audit.loading} data-testid="shipping-finder-btn"
            title="Find published products on high-shipping print providers so you can Fix & republish them onto the cheapest one"
            className={`rounded-md px-3 py-2 text-sm font-medium flex items-center gap-1.5 border transition-colors ${audit.active ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600" : "bg-white border-amber-300 text-amber-700 hover:bg-amber-50"}`}>
            {audit.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
            {audit.active ? "Clear finder" : "High-shipping finder"}
          </button>
        </div>
      </div>

      {(filters.workflow_state || filters.product_type || filters.mode || filters.source_event) && !audit.active && (
        <div className="mb-4 flex items-center gap-2 text-sm" data-testid="filter-summary">
          <span className="text-zinc-600">Showing <b className="text-zinc-900">{products.length}</b> product(s) matching your filters</span>
          <button
            onClick={() => setFilters({ workflow_state: "", product_type: "", mode: "", source_event: "" })}
            data-testid="clear-filters-btn"
            className="h-7 px-2.5 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-50 text-xs font-medium flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        </div>
      )}

      {audit.active && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2 flex-wrap" data-testid="shipping-finder-banner">
          <Truck className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <b>{audit.count} product(s)</b> are on high-shipping providers (US first-item &gt; $10). They're selected below — click <b>Fix &amp; republish</b> to rebuild each onto the cheapest+fastest US provider. Est. shipping saved: <b>${audit.savings.toFixed(2)}</b> across these.
            <span className="block text-[11px] text-amber-700 mt-0.5">Showing only the flagged products. Clear filters are ignored while the finder is on.</span>
          </div>
        </div>
      )}
      {products.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap text-sm">
          <button onClick={selectAllVisible} data-testid="select-all-btn" className="h-8 px-3 rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-xs font-medium">Select all ({products.length})</button>
          {selectedIds.size > 0 && (
            <>
              <span className="text-xs font-semibold text-violet-700" data-testid="selected-count">{selectedIds.size} selected</span>
              <button onClick={publishSelected} disabled={bulkBusy} data-testid="publish-selected-btn" className="h-8 px-3 rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send selected to Printify
              </button>
              <button onClick={fixRepublishSelected} disabled={bulkBusy} data-testid="fix-republish-btn" title="Recenter each design, delete the old listing, and republish a fresh, correctly-sized one" className="h-8 px-3 rounded-md bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5">
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Fix &amp; republish
              </button>
              <button onClick={() => openAlternates([...selectedIds])} data-testid="create-alternates-bulk-btn" title="Reuse each selected design on other product types (mug, tote, phone case, etc.)" className="h-8 px-3 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold flex items-center gap-1.5">
                <Shapes className="w-3.5 h-3.5" /> Create alternates
              </button>
              <div className="flex items-center gap-1 pl-1 border-l border-zinc-200">
                <span className="text-[11px] text-zinc-500">Set mode:</span>
                <button onClick={() => setBulkAutomation("auto")} disabled={bulkBusy} data-testid="bulk-auto-btn" className="h-8 px-2.5 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 text-xs font-medium">Auto-publish</button>
                <button onClick={() => setBulkAutomation("manual")} disabled={bulkBusy} data-testid="bulk-manual-btn" className="h-8 px-2.5 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium">Manual</button>
                <button onClick={() => setBulkAutomation("draft")} disabled={bulkBusy} data-testid="bulk-draft-btn" className="h-8 px-2.5 rounded-md border border-zinc-300 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 text-xs font-medium">Draft</button>
              </div>
              <button onClick={clearSelection} data-testid="clear-selection-btn" className="h-8 px-2.5 rounded-md text-zinc-500 hover:text-zinc-800 text-xs">Clear</button>
            </>
          )}
        </div>
      )}

      {dups.length > 0 && (
        <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50/60 p-4" data-testid="duplicate-panel">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-rose-600 text-white flex items-center justify-center shrink-0"><Copy className="w-5 h-5" /></div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-rose-900">Duplicate listings found</h3>
              <p className="text-xs text-rose-800/80 mt-0.5">{dups.length} group{dups.length > 1 ? "s" : ""} share the same title or source idea. Pick the one to keep — removing the rest also delists them from Printify → Shopify.</p>
            </div>
          </div>
          <div className="mt-3 space-y-3">
            {dups.map((g) => {
              const gkey = g.members[0].id;
              const keep = keepSel[gkey] || g.keep_id;
              const removeCount = g.members.filter((m) => m.id !== keep).length;
              return (
                <div key={gkey} className="rounded-md border border-rose-100 bg-white p-3" data-testid={`dup-group-${gkey}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs font-semibold text-zinc-800 truncate max-w-[70%]">{g.title} <span className="text-rose-600">· {g.count} copies</span></div>
                    <button onClick={() => runDedupe(g)} disabled={dedupeBusy || removeCount === 0} data-testid={`dedupe-btn-${gkey}`}
                      className="h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
                      {dedupeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Remove {removeCount} duplicate{removeCount !== 1 ? "s" : ""}
                    </button>
                  </div>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {g.members.map((m) => (
                      <label key={m.id} data-testid={`dup-member-${m.id}`}
                        className={`flex gap-2 items-center p-2 rounded-md border cursor-pointer transition-colors ${keep === m.id ? "border-emerald-400 bg-emerald-50/60" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                        <input type="radio" name={`keep-${gkey}`} checked={keep === m.id} onChange={() => setKeepSel((s) => ({ ...s, [gkey]: m.id }))} data-testid={`keep-radio-${m.id}`} className="accent-emerald-600" />
                        <img src={productImage(m)} alt="" className="w-10 h-10 rounded object-cover bg-zinc-100 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-medium truncate">{keep === m.id ? "Keep" : "Remove"}</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            {m.live ? <Badge className="text-[9px] border bg-emerald-50 text-emerald-700 border-emerald-200">Live on Shopify</Badge> : <Badge variant="secondary" className="text-[9px]">Not live</Badge>}
                          </div>
                          <div className="text-[10px] text-zinc-400 mt-0.5 truncate">{(m.created_at || "").slice(0, 10)}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {readiness && readiness.summary.total > 0 && (() => {
        const s = readiness.summary;
        const faireOnly = readiness.not_ready.filter((r) => r.storefront_ok && !r.faire_ok);
        const storeBlocked = readiness.not_ready.filter((r) => !r.storefront_ok);
        const allReady = s.storefront_blocked === 0 && s.faire_blocked === 0;
        return (
          <div className={`mb-3 rounded-lg border p-4 ${allReady ? "border-emerald-200 bg-emerald-50/50" : "border-amber-300 bg-amber-50/60"}`} data-testid="publish-readiness-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5 min-w-0">
                {allReady
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-zinc-800">Publish readiness <span className="font-normal text-zinc-400">(unpublished products)</span></h3>
                  <p className="text-xs text-zinc-600 mt-0.5" data-testid="readiness-summary">
                    <b className="text-emerald-700">{s.faire_ready}</b> of {s.total} fully ready for store + Faire
                    {storeBlocked.length > 0 && <> · <b className="text-rose-600">{storeBlocked.length}</b> can't publish anywhere (fix required)</>}
                    {faireOnly.length > 0 && <> · <b className="text-amber-700">{faireOnly.length}</b> will publish to the store but Faire will reject</>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={loadReadiness} data-testid="readiness-refresh-btn" className="h-8 px-2.5 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 text-xs text-zinc-600 flex items-center gap-1.5 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                {readiness.not_ready.length > 0 && (
                  <button onClick={() => setReadinessOpen((v) => !v)} data-testid="readiness-toggle-btn" className="h-8 px-2.5 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 text-xs text-zinc-700 font-medium flex items-center gap-1.5 transition-colors">
                    {readinessOpen ? "Hide" : `Review ${readiness.not_ready.length}`} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${readinessOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
            </div>
            {readinessOpen && readiness.not_ready.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200 space-y-1.5 max-h-72 overflow-y-auto" data-testid="readiness-list">
                {storeBlocked.length > 0 && <div className="text-[11px] font-semibold text-rose-700 uppercase tracking-wide">Fix required — won't publish anywhere</div>}
                {storeBlocked.map((r) => (
                  <div key={r.id} data-testid={`readiness-item-${r.id}`} className="flex items-center gap-2 bg-white border border-rose-200 rounded-md px-2.5 py-1.5">
                    <Badge variant="secondary" className="text-[9px] shrink-0">{TYPE_LABELS[r.product_type]}</Badge>
                    <button onClick={() => setParams({ id: r.id })} className="text-xs font-medium text-zinc-800 hover:text-blue-600 truncate text-left flex-1">{r.title}</button>
                    <span className="text-[11px] text-rose-600 shrink-0">{r.storefront_issues.join(" · ")}</span>
                  </div>
                ))}
                {faireOnly.length > 0 && <div className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide pt-1">Publishes to store — Faire will reject</div>}
                {faireOnly.map((r) => (
                  <div key={r.id} data-testid={`readiness-item-${r.id}`} className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-2.5 py-1.5">
                    <Badge variant="secondary" className="text-[9px] shrink-0">{TYPE_LABELS[r.product_type]}</Badge>
                    <button onClick={() => setParams({ id: r.id })} className="text-xs font-medium text-zinc-800 hover:text-blue-600 truncate text-left flex-1">{r.title}</button>
                    <span className="text-[11px] text-amber-700 shrink-0">{r.faire_issues.join(" · ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {printifyConfigured && pfStatus && (pfStatus.counts.failed > 0 || pfStatus.counts.pending > 0) && (
        <div className="mb-5 rounded-lg border border-rose-300 bg-rose-50/70 p-4" data-testid="printify-failed-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="font-semibold text-sm text-rose-800" data-testid="printify-failed-summary">
                  {pfStatus.counts.failed > 0 && <>{pfStatus.counts.failed} product{pfStatus.counts.failed > 1 ? "s" : ""} failed to publish to Shopify</>}
                  {pfStatus.counts.failed > 0 && pfStatus.counts.pending > 0 && " · "}
                  {pfStatus.counts.pending > 0 && <>{pfStatus.counts.pending} still publishing</>}
                </h3>
                <p className="text-xs text-rose-700/80 mt-1">
                  These were created in Printify but Shopify didn't confirm the listing (often the store-wide variant budget or a stale Printify↔Shopify connection). Fix the cause in Printify/Shopify, then retry — it re-publishes without recreating them.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={refreshPfStatus} disabled={pfBusy} data-testid="printify-refresh-btn"
                className="h-9 px-3 rounded-md border border-rose-300 bg-white hover:bg-rose-50 disabled:opacity-50 text-rose-700 text-xs font-medium flex items-center gap-1.5">
                {pfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh status
              </button>
              {pfStatus.counts.failed > 0 && (
                <button onClick={() => retryFailed(null)} disabled={pfBusy} data-testid="printify-retry-all-btn"
                  className="h-9 px-3 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-medium flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5" /> Retry {pfStatus.counts.failed} failed
                </button>
              )}
            </div>
          </div>
          {pfStatus.failed.length > 0 && (
            <div className="mt-3 space-y-1.5 max-h-56 overflow-auto" data-testid="printify-failed-list">
              {pfStatus.failed.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-3 bg-white rounded-md border border-rose-200 px-3 py-2" data-testid={`printify-failed-${it.id}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] shrink-0">{TYPE_LABELS[it.product_type] || it.product_type}</Badge>
                    <span className="text-xs text-zinc-700 truncate">{it.title}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {it.editor_url && (
                      <a href={it.editor_url} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-400 hover:text-zinc-600 flex items-center gap-1" data-testid={`printify-editor-${it.id}`}>
                        Printify <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button onClick={() => retryFailed([it.id])} disabled={pfBusy} data-testid={`printify-retry-${it.id}`}
                      className="h-7 px-2.5 rounded-md border border-rose-300 bg-white hover:bg-rose-50 disabled:opacity-50 text-rose-700 text-[11px] font-medium flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Retry
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {products.some((p) => p.design_path) && (
        <div className="mb-5 rounded-lg border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 p-4" data-testid="recenter-panel">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0"><AlignCenterVertical className="w-5 h-5" /></div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-violet-900">Recenter existing designs</h3>
                <p className="text-xs text-violet-800/80 mt-0.5">Repositions the artwork on designs you've already built (fixes art sitting too high) and re-renders previews. <span className="font-medium text-violet-700">Free — no new AI art, no credits.</span></p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {recenterJob?.status === "running" ? (
                <>
                  <span className="text-xs text-violet-700 font-medium" data-testid="recenter-progress">{recenterJob.completed || 0}/{recenterJob.total || 0}</span>
                  <span className="h-9 px-3 rounded-md bg-violet-100 text-violet-700 text-sm font-medium flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Recentering…</span>
                  <button onClick={recenterDesigns} data-testid="recenter-resume-btn" title="If it looks stuck (e.g. after a redeploy), resume the remaining designs"
                    className="h-9 px-3 rounded-md border border-violet-300 text-violet-700 hover:bg-violet-100 text-xs font-medium flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Resume</button>
                  <button onClick={clearRecenter} data-testid="recenter-clear-btn"
                    className="h-9 px-3 rounded-md border border-zinc-300 text-zinc-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300 text-xs font-medium flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Clear</button>
                </>
              ) : (
                <button onClick={recenterDesigns} data-testid="recenter-designs-btn"
                  className="h-9 px-4 rounded-md bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors">
                  <AlignCenterVertical className="w-4 h-4" /> Recenter all designs
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {printifyConfigured && (
        <div className="mb-5 rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4" data-testid="batch-publish-panel">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-orange-600 text-white flex items-center justify-center shrink-0"><Rocket className="w-5 h-5" /></div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-orange-900">Publish built products in bulk</h3>
                <p className="text-xs text-orange-800/80 mt-0.5">Publishes your highest-scoring <b>already-built</b> products to Printify → auto-syncs live to Shopify. <span className="text-orange-700 font-medium">{eligibleCount} eligible</span></p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select value={batchMode} onChange={(e) => setBatchMode(e.target.value)} disabled={batchJob?.status === "running"} data-testid="batch-mode-select" className="bg-white border border-orange-200 rounded-md px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400">
                <option value="ready_made">Ready-made only</option>
                <option value="personalized">Personalized only</option>
                <option value="all">All types</option>
              </select>
              <label className="text-xs text-orange-800/80">Top</label>
              <select value={batchN} onChange={(e) => setBatchN(Number(e.target.value))} disabled={batchJob?.status === "running"} data-testid="batch-count-select" className="bg-white border border-orange-200 rounded-md px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400">
                {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button onClick={batchPublish} disabled={batchJob?.status === "running" || eligibleCount === 0} data-testid="batch-publish-btn"
                className="h-9 px-4 rounded-md bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors">
                {batchJob?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Factory className="w-4 h-4" />}
                {batchJob?.status === "running" ? "Publishing…" : "Publish"}
              </button>
            </div>
          </div>

          <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="batch-cat-chips">
            <span className="text-[11px] font-medium text-orange-800/70">Category:</span>
            <button onClick={() => setBatchCats([])} disabled={batchJob?.status === "running"} data-testid="batch-cat-all"
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${batchCats.length === 0 ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
              All
            </button>
            {CATEGORIES.map((cat) => {
              const on = batchCats.includes(cat);
              return (
                <button key={cat} disabled={batchJob?.status === "running"} data-testid={`batch-cat-${cat}`}
                  onClick={() => setBatchCats((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat])}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="batch-theme-chips">
            <span className="text-[11px] font-medium text-orange-800/70">Themes:</span>
            <button onClick={() => setBatchThemes([])} disabled={batchJob?.status === "running"} data-testid="batch-theme-all"
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${batchThemes.length === 0 ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
              All ({themes.length})
            </button>
            {themes.map((t) => {
              const on = batchThemes.includes(t.key);
              return (
                <button key={t.key} disabled={batchJob?.status === "running"} data-testid={`batch-theme-${t.key}`}
                  onClick={() => setBatchThemes((prev) => prev.includes(t.key) ? prev.filter((k) => k !== t.key) : [...prev, t.key])}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-amber-700 text-white border-amber-700" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                  {t.name}
                </button>
              );
            })}
          </div>

          <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="batch-type-chips">
            <span className="text-[11px] font-medium text-orange-800/70">Types:</span>
            <button onClick={() => setBatchTypes([])} disabled={batchJob?.status === "running"} data-testid="batch-type-all"
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${batchTypes.length === 0 ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
              All
            </button>
            {Object.entries(TYPE_LABELS).map(([key, label]) => {
              const on = batchTypes.includes(key);
              return (
                <button key={key} disabled={batchJob?.status === "running"} data-testid={`batch-type-${key}`}
                  onClick={() => setBatchTypes((prev) => prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key])}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                  {label}
                </button>
              );
            })}
          </div>

          <div className="mt-3 pt-3 border-t border-orange-200" data-testid="readymade-pipeline">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h4 className="text-sm font-bold text-orange-900">Build &amp; publish ready-made</h4>
                <p className="text-xs text-orange-800/80 mt-0.5">Generates the print designs and publishes fresh ready-made products in one run. Pick product types and themes to focus the batch. Each design is an AI image.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <label className="text-xs text-orange-800/80 flex items-center gap-1.5">
                  <input type="checkbox" checked={pipelineFresh} onChange={(e) => setPipelineFresh(e.target.checked)} disabled={batchJob?.status === "running"} data-testid="pipeline-fresh-toggle" className="accent-orange-600" />
                  Generate fresh ideas
                </label>
                <select value={pipelineN} onChange={(e) => setPipelineN(Number(e.target.value))} disabled={batchJob?.status === "running"} data-testid="pipeline-count-select" className="bg-white border border-orange-200 rounded-md px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-orange-400">
                  {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <button onClick={runReadymadePipeline} disabled={batchJob?.status === "running"} data-testid="pipeline-run-btn"
                  className="h-9 px-4 rounded-md bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors">
                  {batchJob?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  {batchJob?.status === "running" ? "Working…" : "Build & publish"}
                </button>
              </div>
            </div>
            <div className="mt-2.5 flex items-center gap-2 flex-wrap" data-testid="pipeline-type-chips">
              <span className="text-[11px] font-medium text-orange-800/70">Product types:</span>
              <button onClick={() => setPipelineTypes([])} disabled={batchJob?.status === "running"} data-testid="pipeline-type-all"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${pipelineTypes.length === 0 ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                All ({Object.keys(TYPE_LABELS).length})
              </button>
              {Object.entries(TYPE_LABELS).map(([key, label]) => {
                const on = pipelineTypes.includes(key);
                return (
                  <button key={key} disabled={batchJob?.status === "running"} data-testid={`pipeline-type-${key}`}
                    onClick={() => setPipelineTypes((prev) => prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key])}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-amber-700 text-white border-amber-700" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="pipeline-theme-chips">
              <span className="text-[11px] font-medium text-orange-800/70">Themes:</span>
              <button onClick={() => setPipelineThemes([])} disabled={batchJob?.status === "running"} data-testid="pipeline-theme-all"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${pipelineThemes.length === 0 ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                All ({themes.length})
              </button>
              {themes.map((t) => {
                const on = pipelineThemes.includes(t.key);
                return (
                  <button key={t.key} disabled={batchJob?.status === "running"} data-testid={`pipeline-theme-${t.key}`}
                    onClick={() => setPipelineThemes((prev) => prev.includes(t.key) ? prev.filter((k) => k !== t.key) : [...prev, t.key])}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-amber-700 text-white border-amber-700" : "bg-white text-orange-800 border-orange-200 hover:border-orange-400"}`}>
                    {t.name}
                  </button>
                );
              })}
            </div>
            {pipelineAvail != null && (
              <div className="mt-2.5 text-[11px] text-orange-900/90 bg-orange-100/60 border border-orange-200 rounded-md px-2.5 py-1.5" data-testid="pipeline-availability">
                {(() => {
                  const fromExisting = Math.min(pipelineN, pipelineAvail);
                  const shortfall = Math.max(0, pipelineN - pipelineAvail);
                  return (
                    <span>
                      You want <b>{pipelineN}</b> · <b>{fromExisting}</b> ready from existing ideas
                      {pipelineFresh
                        ? <> · <b>{shortfall}</b> will be generated fresh</>
                        : shortfall > 0
                          ? <> · <span className="text-amber-800 font-medium">{shortfall} short</span> — enable "Generate fresh ideas" to fill the batch</>
                          : null}
                      <span className="text-orange-800/60"> ({pipelineAvail} ready-made idea{pipelineAvail === 1 ? "" : "s"} banked for this filter)</span>
                    </span>
                  );
                })()}
              </div>
            )}
          </div>

          {batchJob && (
            <div className="mt-3 pt-3 border-t border-orange-200" data-testid="batch-progress">
              <div className="flex items-center justify-between text-xs text-orange-900 mb-1.5">
                <span>{batchJob.status === "running" ? "Working — Printify → Shopify…" : batchJob.status === "done" ? "Complete" : batchJob.status === "cancelled" ? "Cancelled" : "Finished with errors"}</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{batchJob.completed || 0}/{batchJob.total || 0}</span>
                  {batchJob.status === "running" && (
                    <button onClick={cancelBatchJob} data-testid="batch-cancel-btn"
                      className="h-6 px-2 rounded border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 text-[11px] font-medium flex items-center gap-1 transition-colors">
                      <X className="w-3 h-3" /> Cancel
                    </button>
                  )}
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-orange-200 overflow-hidden">
                <div className="h-full bg-orange-600 transition-all duration-500" style={{ width: `${batchJob.total ? Math.round(((batchJob.completed || 0) / batchJob.total) * 100) : 0}%` }} />
              </div>
              {batchJob.log?.length > 0 && (
                <div className="mt-2 max-h-24 overflow-y-auto text-[11px] text-orange-800/80 font-mono space-y-0.5">
                  {batchJob.log.slice(-6).map((l, i) => <div key={i}>{l}</div>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {printifyConfigured && (
        <div className="mb-5 rounded-lg border border-sky-200 bg-sky-50/60 p-4" data-testid="republish-stuck-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <Wrench className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-sky-900">Re-publish stuck products</h3>
                <p className="text-xs text-sky-800/80 mt-0.5">Checks every Printify listing's true state and re-publishes any stranded in a <strong>"publishing error"</strong> (the state a bulk reprice can leave behind). Variants-only — your Shopify gallery images stay untouched. Paced to avoid Printify rate limits. Safe to run anytime.</p>
              </div>
            </div>
            <button onClick={republishStuck} disabled={recoverJob?.status === "running"} data-testid="republish-stuck-btn"
              className="h-9 px-4 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors shrink-0">
              {recoverJob?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {recoverJob?.status === "running" ? "Recovering…" : "Re-publish stuck"}
            </button>
          </div>
          {recoverJob && (
            <div className="mt-3" data-testid="republish-stuck-progress">
              <div className="h-2 rounded-full bg-sky-100 overflow-hidden">
                <div className="h-full bg-sky-500 transition-all" style={{ width: `${recoverJob.total ? Math.round((recoverJob.completed / recoverJob.total) * 100) : 0}%` }} />
              </div>
              <div className="text-[11px] text-sky-800 mt-1">
                {recoverJob.completed || 0} / {recoverJob.total || 0} checked
                {recoverJob.status === "done" && recoverJob.result && <> · {recoverJob.result.fixed} re-published, {recoverJob.result.skipped} ok, {recoverJob.result.still_stuck} still failing</>}
              </div>
            </div>
          )}
        </div>
      )}

      {printifyConfigured && (
        <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50/50 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="reset-storefront-panel">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-rose-900">Reset storefront</h3>
            <p className="text-xs text-rose-800/80 mt-0.5">Deletes ALL products from Printify and sweeps leftover Honorifica listings from Shopify (including orphans) so you can re-publish cleanly with corrected print proportions. Your product catalog stays in Heartline.</p>
          </div>
          <button onClick={resetStorefront} disabled={resetBusy} data-testid="reset-storefront-btn"
            className="h-9 px-4 rounded-md bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors shrink-0">
            {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {resetBusy ? "Wiping…" : "Wipe all published"}
          </button>
        </div>
      )}

      <div ref={gridRef} className="grid lg:grid-cols-[380px_1fr] gap-5">
        {/* Master list */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-lg p-12 text-center">
              <Package className="w-8 h-8 text-zinc-300 mx-auto" />
              <p className="text-sm text-zinc-500 mt-3">No products yet. Build one from the Idea Engine.</p>
            </div>
          ) : (
            (audit.active ? products.filter((p) => audit.map[p.id]) : products).map((p) => (
              <div key={p.id} className="flex items-center gap-2">
              <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`product-select-${p.id}`} className="w-4 h-4 accent-violet-600 shrink-0" />
              <button
                onClick={() => setSelected(p)}
                data-testid={`product-row-${p.id}`}
                className={`flex-1 min-w-0 text-left flex gap-3 p-2.5 rounded-lg border transition-all duration-200 ${
                  selected?.id === p.id ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500" : "border-zinc-200 bg-white hover:border-zinc-300"
                }`}
              >
                <img src={productImage(p)} alt="" className="w-14 h-14 rounded-md object-cover bg-zinc-100 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[9px]">{TYPE_LABELS[p.product_type]}</Badge>
                    <Badge className={`text-[9px] border ${workflowBadge[p.workflow_state]} hover:opacity-100`}>{WF[p.workflow_state]}</Badge>
                    {p.mode && <Badge className={`text-[9px] border ${modeBadge[p.mode]} hover:opacity-100`}>{MODE_LABELS[p.mode]}</Badge>}
                    {p.channels?.shopify?.status && <Badge className={`text-[9px] border ${channelStatusBadge[p.channels.shopify.status] || channelStatusBadge.not_listed} hover:opacity-100`}>Shopify · {shopifyStatusLabel[p.channels.shopify.status] || p.channels.shopify.status}</Badge>}
                    {p.channel_fit && (
                      <Badge
                        data-testid={`channel-fit-${p.id}`}
                        title={p.channel_fit === "faire"
                          ? `Faire-viable · ${p.faire_margin}% wholesale margin`
                          : (p.channel_fit_reason === "type_excluded" ? "Type excluded from Faire — sell direct-to-consumer only" : `Wholesale margin ${p.faire_margin}% is below your Faire floor — DTC only`)}
                        className={`text-[9px] border hover:opacity-100 ${p.channel_fit === "faire" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-500 border-zinc-200"}`}>
                        {p.channel_fit === "faire" ? "Faire ✓" : "DTC-only"}
                      </Badge>
                    )}
                    {p.publish_floor_status?.blocked && p.channels?.printify?.status !== "published" && (
                      <Badge
                        data-testid={`floor-block-${p.id}`}
                        title={p.publish_floor_status.no_data
                          ? (p.publish_floor_status.reason === "no_cost" ? "No Printify cost loaded — blocked from publishing until cost is available (override with Publish anyway)" : "No retail price set — blocked from publishing")
                          : `Shipping-absorbed retail margin ${p.publish_floor_status.margin}% is below your ${p.publish_floor_status.floor}% publish floor — blocked (override with Publish anyway)`}
                        className="text-[9px] border hover:opacity-100 bg-rose-50 text-rose-700 border-rose-200">
                        {p.publish_floor_status.no_data ? (p.publish_floor_status.reason === "no_cost" ? "No cost" : "No price") : `Below floor ${p.publish_floor_status.margin}%`}
                      </Badge>
                    )}
                    {audit.map[p.id] && (
                      <Badge
                        data-testid={`high-ship-${p.id}`}
                        title={`On ${audit.map[p.id].provider} — US shipping $${audit.map[p.id].ship_first}. Fix & republish moves it to ${audit.map[p.id].best_provider} ($${audit.map[p.id].best_ship}), saving $${audit.map[p.id].savings}/order.`}
                        className="text-[9px] border hover:opacity-100 bg-amber-100 text-amber-800 border-amber-300">
                        🚚 ${audit.map[p.id].ship_first} → ${audit.map[p.id].best_ship}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs font-semibold mt-1 truncate leading-tight">{p.listing_title}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-zinc-400">{p.theme_name}</span>
                    <span className={`text-[10px] font-bold ${scoreColor(p.impulse_score)}`}>★ {p.impulse_score}</span>
                  </div>
                </div>
              </button>
              </div>
            ))
          )}
        </div>

        {/* Detail / preview */}
        {selected ? (
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="product-detail">
            {/* Mockup + header */}
            <div className="grid md:grid-cols-[220px_1fr] gap-0 border-b border-zinc-200">
              <div className="relative bg-zinc-50 border-r border-zinc-200 group">
                <img
                  src={productImage(selected)}
                  alt="print preview"
                  className="w-full h-full object-cover aspect-square"
                  data-testid="product-print-preview"
                />
                {selected.preview_path && (
                  <span className="absolute top-2 left-2 text-[10px] font-medium bg-emerald-600 text-white rounded-full px-2 py-0.5 shadow-sm" data-testid="preview-badge">
                    True-scale print
                  </span>
                )}
                <div className="absolute bottom-2 right-2">
                  <button onClick={regenerateMockup} disabled={previewBusy} data-testid="regenerate-mockup-btn" className="h-8 px-2.5 rounded-md bg-white/90 backdrop-blur border border-zinc-200 text-xs font-medium flex items-center gap-1.5 hover:bg-white transition-colors shadow-sm">
                    {previewBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {selected.preview_path ? "Regenerate design" : "Generate design"}
                  </button>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="secondary" className="text-[10px]">{TYPE_LABELS[selected.product_type]}</Badge>
                  <Badge className={`text-[10px] border ${workflowBadge[selected.workflow_state]}`}>{WF[selected.workflow_state]}</Badge>
                  {selected.mode && <Badge className={`text-[10px] border ${modeBadge[selected.mode]}`}>{MODE_LABELS[selected.mode]}</Badge>}
                  <span className={`text-xs font-bold ml-auto ${scoreColor(selected.impulse_score)}`}>Impulse {selected.impulse_score}</span>
                </div>
                <h2 className="font-display text-lg font-bold tracking-tight leading-snug">{selected.listing_title}</h2>
                <p className="text-sm text-zinc-500 italic mt-1.5">"{selected.emotional_hook}"</p>
                {selected.channels?.shopify?.url && (
                  <a href={selected.channels.shopify.url} target="_blank" rel="noreferrer" data-testid="open-in-shopify-link"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1 hover:bg-emerald-100 transition-colors">
                    <ExternalLink className="w-3.5 h-3.5" /> Open in Shopify · {shopifyStatusLabel[selected.channels.shopify.status] || selected.channels.shopify.status}
                  </a>
                )}
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-emerald-600" /><span className="font-semibold">${selected.suggested_price}</span><span className="text-xs text-zinc-400">retail</span></div>
                  <div className="text-xs text-zinc-500">cost ${selected.base_cost} · <span className="text-emerald-600 font-medium">${selected.margin_amount} margin</span></div>
                </div>
                {providerInfo?.configured && (providerInfo.provider || providerInfo.best_provider) && (
                  <div className="mt-2 text-xs rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2" data-testid="product-provider-info">
                    <div className="flex items-center gap-1.5 text-zinc-700">
                      <Truck className="w-3.5 h-3.5 text-zinc-500" />
                      {providerInfo.published ? (
                        <span><span className="font-medium">Printer:</span> {providerInfo.provider || "—"} · <span className="font-medium">US ship ${providerInfo.ship_first}</span>{providerInfo.handling ? ` · ~${providerInfo.handling}d production` : ""}</span>
                      ) : (
                        <span className="text-zinc-500">Not on Printify yet — a fresh build would use <span className="font-medium">{providerInfo.best_provider}</span> (US ship ${providerInfo.best_ship})</span>
                      )}
                    </div>
                    {providerInfo.published && providerInfo.savings > 0.5 && (
                      <div className="mt-1 text-amber-700" data-testid="product-provider-savings">
                        Cheaper option: <span className="font-medium">{providerInfo.best_provider}</span> at ${providerInfo.best_ship} — <span className="font-medium">Fix &amp; republish</span> to save ${providerInfo.savings}/order.
                      </div>
                    )}
                  </div>
                )}
                <details className="mt-3">
                  <summary className="text-[11px] text-blue-600 hover:text-blue-700 cursor-pointer select-none" data-testid="product-why">Why impulse {selected.impulse_score}? · score breakdown</summary>
                  <div className="mt-2 max-w-sm"><ScoreBreakdown scores={selected.scores} /></div>
                </details>
              </div>
            </div>

            {/* Workflow controls */}
            <div className="p-5 border-b border-zinc-200 bg-zinc-50/50">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="overline text-zinc-400 mb-2">Automation mode</div>
                  <div className="flex gap-1.5">
                    {AUTOMATION.map((a) => (
                      <button
                        key={a.key}
                        onClick={() => setAutomation(a.key)}
                        data-testid={`automation-${a.key}`}
                        className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${
                          selected.automation_mode === a.key ? "bg-blue-600 text-white border-blue-600" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"
                        }`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Channels & pricing */}
            <ChannelPublish
              product={selected}
              onUpdate={(d) => { setSelected(d); setProducts((prev) => prev.map((p) => (p.id === d.id ? d : p))); }}
            />

            {/* Listing content */}
            <div className="p-5 space-y-5">
              <div className="flex justify-between items-center">
                <button onClick={genSocial} disabled={socialBusy} data-testid="gen-social-btn" className="text-xs text-white bg-zinc-900 hover:bg-zinc-800 disabled:opacity-60 px-3 h-8 rounded-md font-medium flex items-center gap-1.5">
                  {socialBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Generate social posts
                </button>
                <div className="flex items-center gap-3">
                  {selected.design_path && (
                    <button onClick={() => openAlternates([selected.id])} data-testid="create-alternates-btn" title="Reuse this design on other product types" className="text-xs text-white bg-teal-600 hover:bg-teal-700 px-3 h-8 rounded-md font-medium flex items-center gap-1.5">
                      <Shapes className="w-3.5 h-3.5" /> Create alternates
                    </button>
                  )}
                  <button onClick={copyAll} data-testid="copy-all-btn" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5">
                    <Copy className="w-3.5 h-3.5" /> Copy full listing
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="overline text-zinc-400 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Description</div>
                  <CopyBtn text={selected.description} id="desc" />
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed bg-zinc-50 rounded-md p-3 border border-zinc-100">{selected.description}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="overline text-zinc-400 flex items-center gap-1.5"><TagIcon className="w-3.5 h-3.5" /> Listing tags ({(selected.tags || []).length}/13)</div>
                  <CopyBtn text={(selected.tags || []).join(", ")} id="tags" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.tags || []).map((t, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100">{t}</span>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="overline text-zinc-400 mb-1.5 flex items-center gap-1.5"><Sliders className="w-3.5 h-3.5" /> Customization schema</div>
                  <div className="space-y-1.5">
                    {(selected.customization_schema || []).map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-zinc-50 rounded-md px-2.5 py-1.5 border border-zinc-100">
                        <span className="font-medium text-zinc-700">{c.label || c.field}</span>
                        <span className="text-zinc-400">{c.type}{c.required ? " · required" : ""}</span>
                      </div>
                    ))}
                    {(!selected.customization_schema || selected.customization_schema.length === 0) && (
                      <p className="text-xs text-zinc-400">{selected.personalization_required === false ? "Ready-made — no buyer input needed." : "No customization fields."}</p>
                    )}
                  </div>
                </div>
                <div>
                  <div className="overline text-zinc-400 mb-1.5">Variants &amp; materials</div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(selected.variants || []).map((v, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-md bg-zinc-100 text-zinc-600">{v.name}: {v.option}</span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.materials || []).map((m, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border border-zinc-200 text-zinc-500">{m}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-zinc-100 flex justify-end">
                <button onClick={remove} data-testid="delete-product-btn" className="text-xs text-rose-500 hover:text-rose-600 flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Delete product
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-zinc-300 rounded-lg flex flex-col items-center justify-center py-24">
            <ImageIcon className="w-10 h-10 text-zinc-300" />
            <p className="text-sm text-zinc-500 mt-3">Select a product to preview its listing &amp; mockup.</p>
          </div>
        )}
      </div>

      {altJob && ["running", "done"].includes(altJob.status) && (
        <div className="fixed bottom-4 right-4 z-40 bg-white border border-teal-200 shadow-lg rounded-lg px-4 py-3 text-sm flex items-center gap-2" data-testid="alternates-progress">
          <Shapes className="w-4 h-4 text-teal-600" />
          <span className="text-zinc-700">{altJob.status === "done" ? `Created ${altJob.result?.created || 0} alternate(s)` : `Creating alternates… ${altJob.completed || 0}/${altJob.total || 0}`}</span>
        </div>
      )}

      {altModal.open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="alternates-modal" onClick={() => setAltModal({ open: false, sourceIds: [], types: new Set() })}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-zinc-100">
              <div>
                <h3 className="text-base font-semibold text-zinc-900 flex items-center gap-2"><Shapes className="w-4 h-4 text-teal-600" /> Create alternates</h3>
                <p className="text-xs text-zinc-500 mt-1">Reuse {altModal.sourceIds.length === 1 ? "this design" : `${altModal.sourceIds.length} designs`} on the product types below. The exact artwork is reused (no new AI image), with fresh per-type listing text and pricing. New products appear here as <span className="font-medium">"Needs review"</span> before publishing.</p>
              </div>
              <button onClick={() => setAltModal({ open: false, sourceIds: [], types: new Set() })} data-testid="alternates-close-btn" className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(TYPE_LABELS).map(([key, label]) => {
                  const on = altModal.types.has(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleAltType(key)}
                      data-testid={`alt-type-${key}`}
                      className={`h-10 px-3 rounded-md border text-xs font-medium flex items-center gap-2 transition-colors ${on ? "border-teal-500 bg-teal-50 text-teal-800" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-teal-600 border-teal-600" : "border-zinc-300"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between p-5 border-t border-zinc-100">
              <span className="text-xs text-zinc-500" data-testid="alt-count">{altModal.types.size} type(s) · {altModal.types.size * altModal.sourceIds.length} new product(s)</span>
              <button onClick={submitAlternates} disabled={!altModal.types.size} data-testid="alternates-submit-btn" className="h-9 px-4 rounded-md bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5">
                <Shapes className="w-4 h-4" /> Create {altModal.types.size * altModal.sourceIds.length || ""} alternate{altModal.types.size * altModal.sourceIds.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
